import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Badge, Button, Card, CardBody, CardHeader, Col, Row, Spinner, Table, Tooltip} from "reactstrap";
import {DataSources} from "../../services/api/DataSources";
import {apiErrorRaised, useApiErrorDispatch} from "../../context/api_error/ApiErrorIndex";
import DataSourceDeleteButton from "./DataSourceDeleteButton";
import {Link} from "react-router-dom";
import {DataSourceStepButtonGroup} from "./DataSourceStepButtonGroup/DataSourceStepButtonGroup";
import {useProjectState} from "../../context/project/ProjectIndex";
import {DataSourceTypeImage} from "./DataSourceSettings/DataSourceTypeImage";

const DataSourceDetail = ({companyId, projectId, dataSourceId}) => {
  const projectState = useProjectState()
  const apiErrorDispatch = useApiErrorDispatch();
  const dispatch = useApiErrorDispatch();
  const [isLoading, setLoading] = useState(false)
  const [dataSource, setDataSource] = useState({});
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    DataSources.get(companyId, projectId, dataSourceId).then((r) => {
      if (r?.status < 400) {
        setDataSource(r.data);
        setLoading(false);
      } else {
        apiErrorRaised(apiErrorDispatch, r?.response)
      }
    });
  }, [dataSourceId, dispatch, apiErrorDispatch, companyId, projectId]);

  const handleToggle = useCallback((e) => {
    const {name, checked} = e.target;

    if (checked) {
      DataSources.activate(companyId, projectId, dataSourceId).then((r) => {
        if (r?.status < 400) {
          setDataSource(prevDataSource => ({...prevDataSource, [name]: checked}));
        } else {
          apiErrorRaised(apiErrorDispatch, r?.response);
        }
      });
    } else {
      DataSources.disable(companyId, projectId, dataSourceId).then((r) => {
        if (r?.status < 400) {
          setDataSource(prevDataSource => ({...prevDataSource, [name]: checked}));
        } else {
          apiErrorRaised(apiErrorDispatch, r?.response);
        }
      });
    }
  }, [companyId, projectId, dataSourceId, apiErrorDispatch]);

  const handleCopyUrl = useCallback(() => {
    if (dataSource.url) {
      navigator.clipboard.writeText(dataSource.url).then(() => {
        setCopyTooltipOpen(true);
        setTimeout(() => setCopyTooltipOpen(false), 1500);
      });
    }
  }, [dataSource.url]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '400px'}}>
        <Spinner color="primary"/>
      </div>
    )
  }

  return (
    <>
      <div className="d-flex flex-column">
        <div className="d-flex flex-row justify-content-between mb-4">
          <div className="d-flex flex-row align-items-center">
            <Link
              to={`/admin/companies/${projectState.companyId}/projects/${projectState.projectId}/data_sources/`}>
              <Button className="btn-icon-only text-dark">
                <span className="fa fa-arrow-left"/>
              </Button>
            </Link>
            <h1 className="ms-4 mb-0">Data Source Details</h1>
          </div>
          <div className="d-flex align-items-center">
            <DataSourceStepButtonGroup id={dataSourceId} step="details"/>
            <div className={'ms-5'}>
              <DataSourceDeleteButton id={dataSourceId}/>
            </div>

          </div>
        </div>

        <Card className="shadow mb-4">
          <CardHeader className="bg-white py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">{dataSource.name}</h3>
              <div className="d-flex align-items-center">
                <label className="custom-toggle me-3 mb-0">
                  <input type="checkbox" name="status" checked={dataSource.status} onChange={handleToggle}/>
                  <span className="custom-toggle-slider rounded-circle"/>
                </label>
                <Badge className={'p-2 pe-3 ps-3'} color={dataSource.status === true ? "success" : "warning"} pill>
                  {dataSource.status === true ? 'ACTIVE' : 'PAUSED'}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className={'d-flex justify-content-start'}>
              <DataSourceTypeImage
                type={dataSource.data?.extension || dataSource.data?.platform}
                status={dataSource.status}
                size={'large'}
              />
            </div>
            <Row className="mb-4">
              <Col lg="6" className="mb-3 mb-lg-0">
                <Card className="h-100 border">
                  <CardHeader className="">
                    <h5 className="mb-0">Source Information</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <small className="text-uppercase text-muted font-weight-bold d-block mb-1">URL</small>
                      <div className="d-flex align-items-center">
                        <p className="text-truncate mb-0"
                           ref={urlRef}>{dataSource.url || "—"}</p>
                        {dataSource.url && (
                          <>
                            <span
                              id="datasource-copy-url"
                              className="fa fa-copy cursor-pointer text-primary me-2 ms-2"
                              onClick={handleCopyUrl}
                              style={{cursor: 'pointer'}}
                            />
                            <a
                              href={dataSource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary"
                            >
                              <span className="fa fa-external-link"/>
                            </a>
                            <Tooltip
                              placement="top"
                              target="datasource-copy-url"
                              isOpen={copyTooltipOpen}
                            >
                              Copied
                            </Tooltip>
                          </>
                        )}

                      </div>
                    </div>
                    {
                      dataSource.order !== 1 ?

                        <div className={'mb-3'}>
                          <small className="text-uppercase text-muted font-weight-bold d-block mb-1">Combine
                            Field</small>
                          <p className="mb-0 me-2 text-wrap">
                            {dataSource.combineField}
                          </p>
                        </div>
                        : <></>
                    }
                    <Card className="border">
                      <CardHeader className="">
                        <h5 className="mb-0">Field Mapping</h5>
                      </CardHeader>
                      <CardBody className="p-0">
                        {dataSource.mapping && dataSource.mapping.length > 0 ? (
                          <Table className="align-items-center mb-0" responsive>
                            <thead className="thead-light">
                            <tr>
                              <th scope="col" className="ps-3">Origin</th>
                              <th scope="col">Destination</th>
                            </tr>
                            </thead>
                            <tbody>
                            {dataSource.mapping.map((item, key) => (
                              <tr key={key}>
                                <td className="ps-3">{item.origin}</td>
                                <td>{item.destination}</td>
                              </tr>
                            ))}
                            </tbody>
                          </Table>
                        ) : (
                          <div className="text-center py-4">No mapping available</div>
                        )}
                      </CardBody>
                    </Card>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="6">
                <Card className="h-100 border">
                  <CardHeader className="">
                    <h5 className="mb-0">Dynamic Properties</h5>
                  </CardHeader>
                  <CardBody className="p-0">
                    {dataSource.dynamicProperties && dataSource.dynamicProperties.length > 0 ? (
                      <Table className="align-items-center mb-0" responsive>
                        <thead className="thead-light">
                        <tr>
                          <th scope="col" className="ps-3">Name</th>
                        </tr>
                        </thead>
                        <tbody>
                        {dataSource.dynamicProperties.map((property, key) => (
                          <tr key={key}>
                            <td className="ps-3">{property}</td>
                          </tr>
                        ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-center py-4">No dynamic properties available</div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col lg="6">
                <Card className="h-100 border">
                  <CardHeader className="">
                    <h5 className="mb-0">Import Properties</h5>
                  </CardHeader>
                  <CardBody className="p-0">
                    {dataSource.importProperties && Object.keys(dataSource.importProperties).length > 0 ? (
                      <Table className="align-items-center mb-0" responsive>
                        <thead className="thead-light">
                        <tr>
                          <th scope="col" className="ps-3">Key</th>
                          <th scope="col">Value</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Object.entries(dataSource.importProperties).map(([key, value]) => (
                          <tr key={key}>
                            <td className="ps-3">{key}</td>
                            <td>{value}</td>
                          </tr>
                        ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-center py-4">No import properties available</div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

export default DataSourceDetail;
